export type WorkflowApprovalMode = 'MANUAL' | 'AUTOMATIC';

export type WorkflowPolicyRole = {
  roleCode: string;
  descriptor: string;
};

export type WorkflowPolicy = {
  id: string;
  processType: string;
  action: string;
  roleCode: string;
  roleDescriptor?: string;
  approvalMode: WorkflowApprovalMode;
  scopeRule: string;
  effectiveFrom: string;
  effectiveTo?: string;
  dueAfterMinutes?: number;
  reminderAfterMinutes?: number;
  reminderEveryMinutes?: number;
  maxReminders: number;
  escalateAfterMinutes?: number;
  escalationRoleCode?: string;
  enabled: boolean;
  version: number;
};

export type WorkflowPolicyUpdateInput = {
  version?: number;
  approvalMode?: WorkflowApprovalMode;
  roleCode?: string;
  dueAfterMinutes?: number | null;
  reminderAfterMinutes?: number | null;
  reminderEveryMinutes?: number | null;
  maxReminders: number;
  escalateAfterMinutes?: number | null;
  escalationRoleCode: string;
  enabled: boolean;
};

export type WorkflowDelegation = {
  id: string;
  delegatorId: string;
  delegatorDescriptor?: string;
  delegateId: string;
  delegateDescriptor?: string;
  processType?: string;
  startsOn: string;
  endsOn?: string;
  createdAt: string;
  revokedAt?: string;
};

export type WorkflowDelegationInput = {
  delegatorId: string;
  delegateId: string;
  processType: string;
  startsOn: string;
  endsOn: string;
  reason: string;
};

export type WorkflowMaintenanceResult = {
  reminders: number;
  escalations: number;
};

export type WorkflowOperationsRow = {
  id: string;
  kind: 'POLICY' | 'DELEGATION';
  descriptor: string;
  assignment: string;
  period: string;
  status: string;
  raw: WorkflowPolicy | WorkflowDelegation;
};

const processLabels: Record<string, string> = {
  CONTACT_CHANGE: '개인정보 변경', JOB_CHANGE: '인사 발령', ORGANIZATION_CHANGE: '조직 변경',
  STAFFING_PLAN: '조직·인사 발령안',
  TIME_OFF: '휴가 신청', TIME_OFF_CANCELLATION: '휴가 취소', HIRE: '입사',
  LEAVE_OF_ABSENCE: '휴직', RETURN_FROM_LEAVE: '복직', END_WORKER: '퇴사',
  POSITION_HEADCOUNT_CHANGE: '포지션·정원 변경', WORKFLOW_REQUEST: '워크플로우 요청',
};

export const workflowProcessOptions = Object.entries(processLabels).map(([value, label]) => ({ value, label }));

export function workflowProcessLabel(processType: string): string {
  return processLabels[processType] ?? '기타 업무';
}

export function workflowApprovalModeLabel(mode: WorkflowApprovalMode): string {
  return mode === 'AUTOMATIC' ? '바로 승인' : '승인자 지정';
}

export function workflowPolicyRoleLabel(policy: WorkflowPolicy, roles: readonly WorkflowPolicyRole[] = []): string {
  if (policy.approvalMode === 'AUTOMATIC') return '불필요';
  return roles.find((role) => role.roleCode === policy.roleCode)?.descriptor
    || policy.roleDescriptor
    || ({ HEYDAY_ADMIN: '시스템 관리자', HR_ADMIN: '인사 관리자', PEOPLE_MANAGER: '조직장' } as Record<string, string>)[policy.roleCode]
    || '지정된 승인 역할';
}

export function workflowPolicySettingsInput(policy: WorkflowPolicy): WorkflowPolicyUpdateInput {
  return {
    version: policy.version,
    dueAfterMinutes: policy.dueAfterMinutes ?? null,
    reminderAfterMinutes: policy.reminderAfterMinutes ?? null,
    reminderEveryMinutes: policy.reminderEveryMinutes ?? null,
    maxReminders: policy.maxReminders,
    escalateAfterMinutes: policy.escalateAfterMinutes ?? null,
    escalationRoleCode: policy.escalationRoleCode ?? '',
    enabled: policy.enabled,
  };
}

export function workflowPolicyRow(item: WorkflowPolicy): WorkflowOperationsRow {
  return {
    id: item.id,
    kind: 'POLICY',
    descriptor: workflowProcessLabel(item.processType),
    assignment: workflowPolicyRoleLabel(item),
    period: `${item.effectiveFrom}${item.effectiveTo ? ` ~ ${item.effectiveTo}` : ' ~'}`,
    status: item.enabled ? 'ACTIVE' : 'DISABLED',
    raw: item,
  };
}

export function workflowDateInSeoul(now = new Date()): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(now);
}

export function shiftWorkflowDate(value: string, days: number): string {
  const date = new Date(`${value}T00:00:00Z`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isFinite(date.valueOf()) || date.toISOString().slice(0, 10) !== value) return '';
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function workflowDelegationStatus(item: WorkflowDelegation, today = workflowDateInSeoul()): string {
  if (item.revokedAt) return 'REVOKED';
  if (today < item.startsOn) return 'SCHEDULED';
  if (item.endsOn && today >= item.endsOn) return 'EXPIRED';
  return 'ACTIVE';
}

export function workflowDelegationRow(item: WorkflowDelegation): WorkflowOperationsRow {
  return {
    id: item.id,
    kind: 'DELEGATION',
    descriptor: item.processType ? workflowProcessLabel(item.processType) : '모든 업무',
    assignment: `${item.delegatorDescriptor || '위임자'} → ${item.delegateDescriptor || '수임자'}`,
    period: `${item.startsOn}${item.endsOn ? ` ~ ${shiftWorkflowDate(item.endsOn, -1)}` : ' ~ 종료일 없음'}`,
    status: workflowDelegationStatus(item),
    raw: item,
  };
}

export function applyFixtureWorkflowPolicyUpdate(
  item: WorkflowPolicy,
  input: WorkflowPolicyUpdateInput,
): WorkflowPolicy {
  const updated: WorkflowPolicy = {
    ...item,
    maxReminders: input.maxReminders,
    enabled: input.enabled,
    version: item.version + 1,
    ...(input.approvalMode === undefined ? {} : { approvalMode: input.approvalMode }),
    ...(input.roleCode === undefined ? {} : { roleCode: input.roleCode, roleDescriptor: undefined }),
  };
  if (updated.approvalMode === 'AUTOMATIC') {
    updated.roleCode = '';
    delete updated.roleDescriptor;
  }
  for (const field of [
    'dueAfterMinutes',
    'reminderAfterMinutes',
    'reminderEveryMinutes',
    'escalateAfterMinutes',
  ] as const) {
    const value = input[field];
    if (value == null) delete updated[field];
    else updated[field] = value;
  }
  if (input.escalationRoleCode.trim()) updated.escalationRoleCode = input.escalationRoleCode.trim();
  else delete updated.escalationRoleCode;
  return updated;
}

export function workflowOperationsStatusLabel(value: string): string {
  return ({
    ACTIVE: '사용 중',
    DISABLED: '사용 안 함',
    REVOKED: '회수됨',
    SCHEDULED: '시작 예정',
    EXPIRED: '기간 만료',
  } as Record<string, string>)[value] ?? value;
}

export function workflowOperationsStatusTone(value: string): 'success' | 'warning' | 'neutral' {
  if (value === 'ACTIVE') return 'success';
  return 'neutral';
}
